import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  selectIsStandardReportStateFresh,
  selectStandardReportClParameterValue,
  selectStandardReportDateState,
  selectStandardReportEndDate,
  selectStandardReportLocationDropdownData,
  selectStandardReportLocationState,
  selectStandardReportSelectedLocation,
  selectStandardReportShowLocationDropdown,
  selectStandardReportStartDate,
  selectStandardReportTabIndex,
} from './selectors';

/** The value type a memoised selector projects, so the streams stay typed by the selectors themselves. */
type SelectedBy<S> = S extends (state: object) => infer R ? R : never;

/**
 * The ten Standard Report state streams the Standard Report and Weekly Report screens read, one
 * per selector, named as the components expose them to their templates.
 */
export interface StandardReportStreams {
  /** Index of the selected report tab. */
  selectedTabIndex$: Observable<SelectedBy<typeof selectStandardReportTabIndex>>;
  /** Whether the saved state is less than an hour old. */
  isStateFresh$: Observable<SelectedBy<typeof selectIsStandardReportStateFresh>>;
  /** The location picked in the report filters. */
  selectedLocation$: Observable<SelectedBy<typeof selectStandardReportSelectedLocation>>;
  /** Options of the location dropdown. */
  locationDropdownData$: Observable<SelectedBy<typeof selectStandardReportLocationDropdownData>>;
  /** Whether the location dropdown is shown. */
  showLocationDropdown$: Observable<SelectedBy<typeof selectStandardReportShowLocationDropdown>>;
  /** The `cl_parameter` value (`'C'` by default). */
  clParameterValue$: Observable<SelectedBy<typeof selectStandardReportClParameterValue>>;
  /** The location-related slice, or null when nothing is saved. */
  locationState$: Observable<SelectedBy<typeof selectStandardReportLocationState>>;
  /** Saved start date, or null. */
  startDate$: Observable<SelectedBy<typeof selectStandardReportStartDate>>;
  /** Saved end date, or null. */
  endDate$: Observable<SelectedBy<typeof selectStandardReportEndDate>>;
  /** The date-range slice, or null when nothing is saved. */
  dateState$: Observable<SelectedBy<typeof selectStandardReportDateState>>;
}

/**
 * Selects the Standard Report state streams from the store, so the Standard Report and Weekly
 * Report components wire their observable fields from one place instead of each repeating the ten
 * `store.select(...)` calls. Pure: it only calls `store.select`, no subscription is made.
 * @param store - The NgRx store.
 * @returns The ten streams, keyed by the components' field names.
 */
export function selectStandardReportStreams(store: Store): StandardReportStreams {
  return {
    selectedTabIndex$: store.select(selectStandardReportTabIndex),
    isStateFresh$: store.select(selectIsStandardReportStateFresh),
    selectedLocation$: store.select(selectStandardReportSelectedLocation),
    locationDropdownData$: store.select(selectStandardReportLocationDropdownData),
    showLocationDropdown$: store.select(selectStandardReportShowLocationDropdown),
    clParameterValue$: store.select(selectStandardReportClParameterValue),
    locationState$: store.select(selectStandardReportLocationState),
    startDate$: store.select(selectStandardReportStartDate),
    endDate$: store.select(selectStandardReportEndDate),
    dateState$: store.select(selectStandardReportDateState),
  };
}
